import { api } from "./client";

export interface PaymentChannel {
  id: number;
  pay_name: string;
}
export interface FundRecord {
  type: string | number;
  create_time: string;
  amount: number;
  before_amount: number;
  after_amount: number;
}
export interface WithdrawalRecord {
  amount: number;
  real_amount: number;
  state: string | number;
  create_time_format: string;
}
export interface WithdrawalSummary {
  balance: number;
  reviewBalance: number;
  drawBalance: number;
}
export interface BankRecord {
  firstName: string;
  phone: string;
  bankName: string;
  bankCode: string;
  bankCard: string;
  ext?: string;
  bankId?: string | number;
}
export interface BankOption {
  code: string;
  name: string;
}
export interface TeamMember {
  id: string | number;
  phone: string;
  is_recharge: boolean | number;
}

const parseQuickAmounts = (value: unknown): number[] => {
  let entries: unknown[] = [];

  if (Array.isArray(value)) {
    entries = value;
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed) as unknown;
      entries = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      entries = trimmed.split(/[,;|\s]+/);
    }
  } else if (value !== null && value !== undefined) {
    entries = [value];
  }

  return [...new Set(entries.map(Number).filter((amount) => Number.isFinite(amount) && amount > 0))];
};

export const walletApi = {
  paymentChannels: async () =>
    (await api.post<{ list: PaymentChannel[] }>("/api/v2/pay/list")).list,
  quickAmounts: async () =>
    parseQuickAmounts(
      (await api.post<{ list: unknown }>("/api/v2/quick/list")).list,
    ),
  exchangeRate: async () =>
    Number(
      (await api.post<{ rate: number | string }>("/api/v2/exchange/rate"))
        .rate || 1,
    ),
  createPayment: (amount: number, channelID: number, orderType: 1 | 2 = 1) =>
    api.post<{ url: string }>("/api/v2/payment", {
      amount,
      channelID,
      orderType,
    }),
  transferTeamBalance: (amount: number) =>
    api.post("/api/v2/exchange/balance", { amount }),
  getUser: () => api.post<Record<string, unknown>>("/api/v2/get/user"),
  drawHint: () =>
    api.post<{ isBindUSDT?: boolean; isBindBank?: boolean }>(
      "/api/v2/draw/hint",
    ),
  createWithdrawal: (amount: number, type: number) =>
    api.post("/api/v2/draw", { amount, type, model: 1 }),
  fundRecords: async (page = 1, num = 20) =>
    (
      await api.post<{ list: FundRecord[] }>("/api/v2/funds/record", {
        type: 1,
        page,
        num,
      })
    ).list,
  fundRecordsByType: async (type: 1 | 2 | 3, page = 1, num = 30) =>
    (
      await api.post<{ list: FundRecord[] }>("/api/v2/funds/record", {
        type,
        page,
        num,
      })
    ).list,
  withdrawalSummary: () => api.post<WithdrawalSummary>("/api/v2/draw/money"),
  withdrawalRecords: (page = 1, num = 20) =>
    api.post<WithdrawalRecord[]>("/api/v2/draw/record", { page, num }),
  userBank: () =>
    api.post<{
      bank: BankRecord | null;
      isAllowEdit?: boolean;
      isAllowDel?: boolean;
    }>("/api/v2/user/bank"),
  bankOptions: async () => {
    const value = (
      await api.post<{ bankList: BankOption[] | string }>("/api/v2/bank/conf")
    ).bankList;
    return typeof value === "string"
      ? (JSON.parse(value) as BankOption[])
      : value;
  },
  saveBank: (bank: BankRecord) => api.post("/api/v2/bind/bank", bank),
  teamCount: () =>
    api.post<{ one: number; two: number; three: number }>("/api/v2/team/count"),
  teamMembers: async (level: number) =>
    (
      await api.post<{ list: TeamMember[] }>("/api/v2/team/user", {
        page: 1,
        num: 20,
        level,
      })
    ).list,
  inviteInfo: () => api.post<{ inviteUrl?: string }>("/api/v2/get/user"),
};
